import clsx from 'clsx';
import { CSSProperties } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { X } from 'react-feather';
import { useInView } from 'react-intersection-observer';

import { noopFunction } from '@vestico/utils';

import { WidgetBubbleLayoutConfigurationDefaults, WidgetItemAspectRatio, WidgetPopupLayoutConfiguration, WidgetPopupPosition } from '@vestico/model';

import { BlurredBackgroundText, Button, GlobalsContext, useLowPowerMode } from '@vestico/shared';

import { CarouselItem } from '../common/CarouselItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import style from './PopupLayout.module.css';

export interface PopupLayoutProps extends BaseLayoutProps {
  layout: WidgetPopupLayoutConfiguration;
  onAppear?: () => void;
}

const widgetCSSPropertiesForPosition = (position?: WidgetPopupPosition) => {
  const css: CSSProperties = {};

  if (!position) {
    return css;
  }
  // position.horizontal = 'left';
  const { horizontal, vertical, marginHorizontal, marginVertical } = position;
  switch (horizontal) {
    case 'left':
      css.left = marginHorizontal;
      css.right = 'auto';
      css.flexDirection = 'row';
      break;
    case 'right':
      css.left = 'auto';
      css.right = marginHorizontal;
      break;
  }

  switch (vertical) {
    case 'top':
      css.top = marginVertical;
      css.bottom = 'auto';
      break;
    case 'bottom':
      css.top = 'auto';
      css.bottom = marginVertical;
      break;
    case 'center':
      css.top = `calc(50% + ${marginVertical}px)`;
      css.transform = 'translate(0,-50%)';
      break;
  }

  return css;
};

export const PopupLayout = ({ layout, responses, onResponseClick, onAppear }: PopupLayoutProps) => {
  const { position, trigger, textBubble } = layout;

  const { ref, inView } = useInView();

  const delay = useMemo(() => (trigger?.delay ?? 0) * 1000, [trigger?.delay]);
  const itemSize = useMemo(
    () => (layout.itemSize && !Number.isNaN(layout.itemSize) ? layout.itemSize : WidgetBubbleLayoutConfigurationDefaults.itemSize),
    [layout]
  );

  const itemStyle = useMemo(() => layout.style ?? 'round', [layout.style]);
  const itemBorderWidth = useMemo(() => layout.itemBorderWidth ?? WidgetBubbleLayoutConfigurationDefaults.itemBorderWidth, [layout.itemBorderWidth]);

  const itemShadowEnabled = useMemo(
    () => (layout.itemShadowEnabled !== undefined ? layout.itemShadowEnabled : WidgetBubbleLayoutConfigurationDefaults.itemShadowEnabled === 1),
    [layout.itemShadowEnabled]
  );

  const aspectRatio: WidgetItemAspectRatio = useMemo(
    () => (itemStyle === 'square' ? layout.aspectRatio ?? 'square' : 'square'),
    [itemStyle, layout.aspectRatio]
  );

  const handleResponseClick = useCallback((index: number) => onResponseClick(index), [onResponseClick]);

  const isLowPowerMode = useLowPowerMode();
  const autoPlayVideoEnabled = useMemo(() => !!layout.autoplayVideo && !isLowPowerMode, [isLowPowerMode, layout.autoplayVideo]);

  const [hidden, setHidden] = useState(delay > 0);
  const [userHidden, setUserHidden] = useState(false);

  useEffect(() => {
    if (delay === 0 || !hidden || userHidden) {
      return noopFunction;
    }

    const timeoutId = setTimeout(() => setHidden(false), delay);
    return () => clearTimeout(timeoutId);
  }, [delay, hidden, userHidden]);

  useEffect(() => {
    if (inView && !hidden && onAppear) {
      onAppear();
    }
  }, [hidden, inView, onAppear]);

  const customCSS = useMemo(() => {
    const css: CSSProperties = widgetCSSPropertiesForPosition(position);
    return css;
  }, [position]);

  const itemBorderRadius = useMemo(() => layout.itemBorderRadius, [layout.itemBorderRadius]);

  const { styles, borderRadiusStyles } = useCarouselStyles({
    borderRadius: itemStyle === 'round' ? '50%' : itemBorderRadius,
    itemSize,
    aspectRatio,
    customCSS,
  });

  const responseMarkup = useMemo(() => {
    if (responses.length === 0) {
      return null;
    }

    const response = responses[0];

    const item = (
      <CarouselItem
        key={response.postDocumentID}
        index={0}
        response={response}
        onClick={handleResponseClick}
        hideVideoIcon
        customCSS={{
          borderWidth: itemBorderWidth,
          filter: `${!itemShadowEnabled ? 'none' : 'drop-shadow(var(--overlay-text-shadow))'}`,
          borderColor: 'var(--color)',
          borderStyle: 'solid',
        }}
        aspectRatio={aspectRatio}
        playVideo={autoPlayVideoEnabled && !hidden}
        borderRadius={borderRadiusStyles}
      />
    );

    return item;
  }, [responses, handleResponseClick, itemBorderWidth, itemShadowEnabled, aspectRatio, autoPlayVideoEnabled, hidden, borderRadiusStyles]);

  const { stylesConfig } = useContext(GlobalsContext);

  const textBubbleMarkup = useMemo(() => {
    if (!textBubble || !textBubble.enabled || textBubble.text.length === 0) {
      return null;
    }

    const { text } = textBubble;

    return (
      <div className={clsx(style.textBubble)}>
        <BlurredBackgroundText color={stylesConfig.stylesConfig.coreColor} corner={position?.horizontal === 'left' ? 'left' : 'right'}>
          <span>{text}</span>
        </BlurredBackgroundText>
      </div>
    );
  }, [position?.horizontal, stylesConfig.stylesConfig.coreColor, textBubble]);

  const onButtonClose = useCallback(() => {
    setUserHidden(true);
    setHidden(true);
  }, []);

  const closePosition = useMemo(() => {
    const radiusVar = itemStyle === 'square' ? itemBorderRadius ?? 0 : itemSize / 2;
    const radius = Math.max(Math.min(radiusVar, itemSize / 2) / 2, 3);
    const angleRadians = (45 * Math.PI) / 180;
    const offset = radius * Math.cos(angleRadians);
    return offset;
  }, [itemBorderRadius, itemSize, itemStyle]);

  const innerContent = responseMarkup ? (
    <>
      {responseMarkup}
      {textBubbleMarkup}
      <div
        className={clsx(style.close, { [style.left]: position?.horizontal === 'left' })}
        style={{ '--close-offset': `${closePosition}px` } as CSSProperties}
      >
        <Button
          content={<X size={18} style={{ marginTop: 2.5 }} />}
          roundedBlur
          onClick={onButtonClose}
          size="xsmall"
          dark
          noPadding
          ariaLabel="close"
          color="#fff"
        />
      </div>
    </>
  ) : null;

  return (
    <div className={clsx(style.root, { [style.hidden]: hidden })} style={styles} ref={ref}>
      {innerContent}
    </div>
  );
};
